/**
 * GWP (Gift With Purchase) cart block.
 * Single file: custom element for cart page + shared UI used by minicart and cart page.
 */

import { h, render, Fragment } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { formatMoney, formatMoneyWithCurrency, globalState } from '../helpers/utils'
import { emitterProps, globalEmitter } from '../helpers/emitter-event'
import { useGwp } from '../hooks/useGwp'
import { CartConfigKeys } from '../hooks/useCartGlobal'
import type { ProcessedGiftWithPurchaseObject } from '../helpers/gwp.types'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'

// --- Helpers ---

/** Maps progress percentage (0–100) to bar width for space-around marker positions */
export function progressBarWidthForSpaceAround(percentage: number, n: number): number {
  if (n <= 0) return 0
  if (n === 1) return Math.min(percentage, 100)
  if (percentage >= 100) return 100
  const p = Math.min(percentage, 100)
  const segmentIndex = Math.min(Math.floor((p * n) / 100), n - 1)
  const segmentStartOld = (segmentIndex * 100) / n
  const segmentEndOld = ((segmentIndex + 1) * 100) / n
  const segmentStartNew = segmentIndex === 0 ? 0 : ((segmentIndex - 0.5) / n) * 100
  const segmentEndNew = ((segmentIndex + 0.5) / n) * 100
  const t = segmentEndOld - segmentStartOld > 0 ? (p - segmentStartOld) / (segmentEndOld - segmentStartOld) : 1
  return segmentStartNew + t * (segmentEndNew - segmentStartNew)
}

// --- GwpProgressBar ---

interface GwpProgressBarProps {
  percentage: number
  gifts: ProcessedGiftWithPurchaseObject[]
  blockSpacing?: boolean
}

export function GwpProgressBar({ percentage, gifts, blockSpacing }: GwpProgressBarProps) {
  const n = gifts?.length ?? 0
  const barWidth = n > 0 ? progressBarWidthForSpaceAround(percentage, n) : 0
  return h(
    'div',
    { className: `${blockSpacing ? 'px-margin lg:px-1.5' : ''} pt-1.5 pb-3.75 border-b border-neutral-medium` },
    h(
      'div',
      { className: 'relative w-full h-[4px] bg-neutral-medium' },
      h('div', {
        className: `absolute inset-tl w-gwp-bar-width h-full transition-all ${gifts.some((g) => g.cartQualifies) ? 'bg-system-success' : 'bg-system-warning'}`,
        style: { '--gwp-bar-width': `${barWidth}%` } as Record<string, string>
      }),
      gifts.map((gift, index) => {
        const leftPercent = gifts.length > 1 ? ((index + 0.5) / gifts.length) * 100 : 50
        return h(
          'div',
          {
            key: gift.id,
            className: `absolute top-1/2 size-[16px] border-[2px] border-neutral-white rounded-full transition-all ${
              gift.cartQualifies ? 'bg-system-success' : 'bg-neutral-medium'
            }`,
            style: { left: `${leftPercent}%`, transform: 'translate(-50%, -50%)' }
          },
          h(
            'div',
            {
              className: `flex flex-col items-center justify-center absolute top-full left-1/2 w-max text-center mt-0.25 -translate-x-1/2 transition-all ${
                gift.cartQualifies ? 'text-brand-black' : 'text-neutral-secondary'
              }`
            },
            h('span', { className: 'paragraph-xs font-medium' }, gift.giftName),
            h(
              'span',
              { className: 'paragraph-xs' },
              gift.cartQualifies
                ? gift.giftType === 'shipping'
                  ? 'Applied'
                  : 'Select Below'
                : gift.minPurchaseType === 'amount'
                  ? `Spend ${formatMoney(gift.minPurchaseAmount).replace('.00', '')}`
                  : `Add ${gift.minPurchaseQuantity} Item${gift.minPurchaseQuantity > 1 ? 's' : ''}`
            )
          )
        )
      })
    )
  )
}

// --- GWP Gift Modal ---

const GWP_MODAL_ID = 'gwp-modal'

function closeGwpModal() {
  const modal = document.querySelector(`x-modal[data-id="${GWP_MODAL_ID}"]`) as any
  if (modal?.handleHide) modal.handleHide()
}

function openGwpGiftModal(giftProducts: preact.ComponentChildren[]) {
  const container = document.querySelector('[data-gwp-modal-inner]')
  if (!container) return

  render(
    h(
      Fragment,
      null,
      h(
        'div',
        {
          className:
            'sticky z-20 flex h-[60px] w-full shrink-0 items-center justify-between gap-x-1 border-b border-neutral-medium bg-secondary px-margin inset-tl md:h-[84px]'
        },
        h('h2', { className: 'h4' }, 'Select Gift'),
        h(
          'button',
          {
            className: 'size-[14px] link-fade children:size-full',
            'aria-label': 'Close gift modal',
            onClick: closeGwpModal
          },
          h(Icon, { id: 'close' })
        )
      ),
      h(
        'div',
        { className: 'flex grow flex-col gap-y-1 overflow-y-auto overscroll-none p-margin' },
        ...giftProducts
      )
    ),
    container as Element
  )

  setTimeout(() => {
    const modal = document.querySelector(`x-modal[data-id="${GWP_MODAL_ID}"]`) as any
    if (modal?.handleToggle) modal.handleToggle()
  }, 100)
}

// --- GwpGiftItem ---

interface GwpGiftItemProps {
  gift: ProcessedGiftWithPurchaseObject
  onAddToCart?: (opts: { loading?: boolean; cart?: unknown }) => void
  isLoading?: boolean
  blockSpacing?: boolean
}

export function GwpGiftItem({ gift, onAddToCart, isLoading, blockSpacing }: GwpGiftItemProps) {
  const loadingState = isLoading ? 'opacity-50' : ''

  const onGwpAddToCart = async (e: Event) => {
    try {
      e.preventDefault()
      const form = new FormData(e.target as HTMLFormElement)
      const id = form.get('id')
      const quantity = form.get('quantity')
      const giftId = form.get('properties[_bl_gwp]')
      onAddToCart?.({ loading: true })
      await (window as any).Cart.addLineItems({ id, quantity, properties: { _bl_gwp: giftId } }, { silent: false })
      await (window as any).Cart.getCart()
      closeGwpModal()
      onAddToCart?.({ loading: false, cart: (window as any).Cart.value })
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: (error as Error).message
      })
      onAddToCart?.({ loading: false })
    }
  }

  if (!gift.customerQualifies) {
    return h(
      'div',
      { className: 'flex flex-col gap-y-0.5 bg-neutral-light px-1 md:px-1.5 py-1.5' },
      h('div', { className: 'paragraph-l font-bold' }, 'Join the club'),
      h(
        'div',
        { className: 'flex flex-col md:flex-row justify-between gap-0.5' },
        h('div', { className: 'paragraph-s' }, `Join the club to get a ${gift.giftName}!`),
        h(
          'div',
          { className: 'paragraph-s font-bold flex flex-row gap-x-0.5' },
          h('a', { href: globalState.routes.account_register_url, className: 'link-fade' }, 'Join\xa0now'),
          h('a', { href: globalState.routes.account_login_url, className: 'link-fade' }, 'Login')
        )
      )
    )
  }

  const giftProducts = gift.giftProducts.map((product) => {
    const featuredImage =
      typeof product.featured_image === 'string'
        ? product.featured_image
        : (product.featured_image as { src?: string })?.src ?? ''
    let discountedPrice = (product.price * (100 - (gift.discountValue as number))) / 100
    const convertedDiscountValue =
      (gift.convertDiscountValue
        ? (gift.discountValue as number) * (window as any).Shopify.currency.rate
        : gift.discountValue) * 100
    switch (gift.discountType) {
      case 'amount':
        discountedPrice = product.price - convertedDiscountValue
        break
      case 'fixed':
        discountedPrice = convertedDiscountValue
        break
    }
    const selectableOptions: Array<{ name: string; number: number }> = []
    if (product.variants.length > 1) {
      product.options.forEach((option, index) => {
        const optionValues = new Set(
          product.variants.map((variant) => (variant as unknown as Record<string, unknown>)[`option${index + 1}`])
        )
        if (optionValues.size > 1) {
          selectableOptions.push({ name: option, number: index + 1 })
        }
      })
    }
    const selectLabel = selectableOptions.length === 1 ? `Select ${selectableOptions[0].name}` : 'Select Variant'

    const variantInput =
      product.variants.length > 1
        ? h(
          'select',
          {
            name: 'id',
            className:
                'flex items-center justify-between w-full h-[36px] px-1 py-0 text-left bg-neutral-white border rounded-8 border-neutral-medium paragraph-b font-medium font-heading uppercase'
          },
          h('option', { value: '', disabled: true, selected: true }, selectLabel),
          ...product.variants.map((variant) => {
            const optionLabel = (
                selectableOptions.length === 1
                  ? (variant as unknown as Record<string, unknown>)[`option${selectableOptions[0].number}`]
                  : variant.title
              ) as string
            return h(
              'option',
              { value: String(variant.id), key: String(variant.id), disabled: !variant.available },
              optionLabel
            )
          })
        )
        : h('input', {
          type: 'hidden',
          name: 'id',
          value: product.variants.find((variant) => variant.available)?.id
        })

    return h(
      'div',
      { key: String(product.id), className: 'flex items-stretch gap-x-1 w-full border border-neutral-medium p-1' },
      h(
        'div',
        { className: 'shrink-0 relative w-full max-w-[90px] aspect-3/4 overflow-hidden' },
        h(
          'a',
          {
            href: product.url,
            className: 'size-full children:size-full children:object-cover link-fade',
            'aria-label': product.title
          },
          h(PreactImage, {
            className: 'size-full object-cover',
            key: String(product.id),
            imageUrl: [featuredImage],
            alt: product.title,
            imageClass: '',
            isLazy: true,
            isCover: true,
            imageSrcSet: [],
            imageBreakpoints: '(min-width: 0px)',
            imageSizes: [150]
          })
        )
      ),
      h(
        'div',
        { className: 'grow flex flex-col gap-y-1 justify-between relative' },
        h(
          'div',
          { className: 'flex lg-max:flex-col justify-between gap-x-0.625' },
          h(
            'a',
            { href: product.url, className: 'paragraph-s font-medium link-fade', 'aria-label': product.title },
            product.title
          ),
          h(
            'div',
            {
              className: 'flex lg:flex-col lg-max:items-center lg:justify-end paragraph-s lg:text-right gap-x-0.5'
            },
            h(
              'span',
              { className: `paragraph-s ${discountedPrice === 0 ? 'text-system-success' : 'text-brand-sale'}` },
              discountedPrice === 0 ? 'Free' : formatMoneyWithCurrency(discountedPrice)
            ),
            discountedPrice < product.price &&
              h(
                'span',
                { className: 'paragraph-s text-neutral-secondary line-through' },
                formatMoneyWithCurrency(product.price)
              )
          )
        ),
        h(
          'form',
          {
            action: globalState.routes.cart_add_url,
            method: 'post',
            onSubmit: (e: Event) => onGwpAddToCart(e),
            className: `flex w-full flex-col items-center gap-0.5 grid-cols-2${product.variants.length > 1 ? ' lg:grid' : ''}`
          },
          variantInput,
          h('input', { type: 'hidden', name: 'quantity', value: 1 }),
          h('input', {
            type: 'hidden',
            name: 'properties[_bl_gwp]',
            value: String(gift.id).split('/').pop()
          }),
          h(
            'button',
            {
              type: 'submit',
              className: 'btn btn-s w-full btn-primary btn-fill',
              'aria-label': 'Add To Bag'
            },
            h('span', null, 'Add To Bag')
          )
        )
      )
    )
  })

  return h(
    'div',
    {
      className: `flex flex-col gap-y-1 ${
        blockSpacing ? 'px-margin md:px-1.5' : ''
      } py-1.5 border-b border-neutral-medium ${loadingState}`
    },
    h('button',
      {
        type: 'button',
        className: 'flex lg-max:flex-col items-start lg:items-center lg:justify-between gap-0.5 w-full p-1 lg:px-2 lg:py-1.5 bg-system-success-light border border-system-success link-fade cursor-pointer',
        'aria-label': 'Select Gift',
        onClick: () => openGwpGiftModal(giftProducts)
      },
      h('p', { className: 'paragraph-b font-medium' }, `You're eligible for a ${gift.giftName}`),
      h('p', { className: 'paragraph-xs font-heading font-medium uppercase text-line text-line-space' }, 'Select Gift')
    )
  )
}

// --- GwpCartBlock (full block: progress bar + alert + next gift) ---

interface GwpCartBlockProps {
  cart: any
  freeShippingMinimum: number
  enableProgressBar?: boolean
  handleSetCartConfig?: (key: string, value: unknown) => void
  isLoading?: boolean
  onCartUpdate?: (cart: unknown) => void
  blockSpacing?: boolean
}

export function GwpCartBlock({
  cart,
  freeShippingMinimum,
  enableProgressBar = true,
  handleSetCartConfig,
  isLoading = false,
  onCartUpdate,
  blockSpacing
}: GwpCartBlockProps) {
  const { giftsForProgressBar, giftsProgressPercentage, nextAvailableGiftWithThreshold, invalidGiftItems } = useGwp(
    cart,
    freeShippingMinimum
  )

  const [removedGifts, setRemovedGifts] = useState<any[]>([])
  const [removingGifts, setRemovingGifts] = useState(false)
  const [localLoading, setLocalLoading] = useState(false)

  const effectiveLoading = handleSetCartConfig ? isLoading : localLoading

  useEffect(() => {
    if (invalidGiftItems.length > 0 && !removingGifts) {
      setRemovingGifts(true)
      const removeItems = async () => {
        try {
          if (handleSetCartConfig) {
            handleSetCartConfig(CartConfigKeys.isLoading, true)
          }
          await (window as any).Cart.updateLineItems(
            invalidGiftItems.reduce((acc: any, item: any) => ({ ...acc, [item.key]: 0 }), {}),
            { silent: true }
          )
          setRemovedGifts(invalidGiftItems)
          await (window as any).Cart.getCart()
          const newCart = (window as any).Cart.value
          if (handleSetCartConfig) {
            handleSetCartConfig(CartConfigKeys.cart, newCart)
            handleSetCartConfig(CartConfigKeys.isLoading, false)
          } else if (onCartUpdate) {
            onCartUpdate(newCart)
          }
        } catch (error) {
          globalEmitter.emit(emitterProps.notice.global, { type: 'error', content: (error as Error).message })
          if (handleSetCartConfig) handleSetCartConfig(CartConfigKeys.isLoading, false)
        } finally {
          setRemovingGifts(false)
        }
      }
      removeItems()
    }
  }, [invalidGiftItems])

  const handleGwpAddToCart = ({ loading, cart: newCart }: { loading?: boolean; cart?: unknown }) => {
    if (handleSetCartConfig) {
      if (loading !== undefined) handleSetCartConfig(CartConfigKeys.isLoading, loading)
      if (newCart) handleSetCartConfig(CartConfigKeys.cart, newCart)
    } else {
      if (loading !== undefined) setLocalLoading(loading)
      if (onCartUpdate && newCart) onCartUpdate(newCart)
    }
  }

  const fragmentChildren: preact.ComponentChildren[] = []
  if (enableProgressBar && giftsForProgressBar?.length > 0) {
    fragmentChildren.push(
      h(GwpProgressBar, { percentage: giftsProgressPercentage, gifts: giftsForProgressBar, blockSpacing })
    )
  }
  if (removedGifts.length > 0) {
    fragmentChildren.push(
      h(
        'div',
        { className: `flex flex-col ${blockSpacing ? 'px-margin lg:px-1.5' : ''} py-1.5` },
        h(
          'div',
          { className: 'flex items-center justify-between gap-x-1 bg-system-error-light p-1 rounded-4' },
          h(
            'p',
            { className: 'paragraph-s font-medium' },
            `You no longer qualify for ${
              removedGifts.length > 1
                ? 'some free gifts and they have been removed'
                : 'a free gift and it has been removed'
            }.`
          ),
          h(
            'button',
            {
              type: 'button',
              className: 'flex size-[14px] children:size-full link-fade',
              'aria-label': 'Close',
              onClick: () => setRemovedGifts([])
            },
            h(Icon, { id: 'close' })
          )
        )
      )
    )
  }
  if (nextAvailableGiftWithThreshold) {
    fragmentChildren.push(
      h(GwpGiftItem, {
        gift: nextAvailableGiftWithThreshold,
        onAddToCart: handleGwpAddToCart,
        isLoading: effectiveLoading,
        blockSpacing
      })
    )
  }

  return h(Fragment, null, ...fragmentChildren)
}

// --- Custom element (cart page mount) ---

interface CartLike {
  items?: unknown[]
  item_count?: number
  [key: string]: unknown
}

const CART_EVENTS = ['renderCartPage', 'renderMiniCart']

function GwpCartBlockContainer() {
  const initCart = (window as any).Cart?.value ?? (globalState as any)?.cart?.initCart ?? null
  const [cart, setCart] = useState<CartLike | null>(initCart)
  const freeShippingMinimum = (globalState as any)?.cart?.freeShippingMinimum ?? 0

  const refreshCart = useCallback(() => {
    if (!(window as any).Cart) return
    ;(window as any).Cart.getCart().then(() => {
      if ((window as any).Cart?.value) {
        setCart({ ...(window as any).Cart.value })
      }
    })
  }, [])

  useEffect(() => {
    if (!cart) refreshCart()

    const handler = () => refreshCart()
    CART_EVENTS.forEach((event) => document.addEventListener(event, handler))
    return () => {
      CART_EVENTS.forEach((event) => document.removeEventListener(event, handler))
    }
  }, [])

  if (!cart) return null

  return h(GwpCartBlock, {
    cart,
    freeShippingMinimum,
    enableProgressBar: (globalState as any)?.cart?.gwp?.enableProgressBar,
    onCartUpdate: (newCart: unknown) => setCart(newCart as CartLike | null)
  })
}

class GwpCartBlockElement extends HTMLElement {
  connectedCallback() {
    this.renderComponent()
  }

  disconnectedCallback() {
    render(null, this)
  }

  private renderComponent() {
    render(h(GwpCartBlockContainer, null), this)
  }
}

export { GwpCartBlockElement }
